import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout, ConfirmDialog } from './ui';
import { api } from '../services/api';

const MicrosoftOAuthConfig = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });

  const [formData, setFormData] = useState({
    client_id: '',
    client_secret: '',
    allowed_domains: '',
    enabled: false
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/oauth/providers/microsoft');
      const data = response.data;
      setConfig(data);
      setFormData({
        client_id: data.client_id || '',
        client_secret: '', // Never show the secret
        allowed_domains: data.allowed_domains || '',
        enabled: data.enabled || false
      });
    } catch (err) {
      setError('Failed to load Microsoft OAuth configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setError('');
      setSuccess('');
      
      await api.post('/admin/oauth/microsoft/configure', formData);
      setSuccess('Microsoft OAuth configuration saved successfully!');
      loadConfig();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save configuration');
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      setError('');

      const response = await api.post('/admin/oauth/microsoft/test');
      setTestResult(response.data);
    } catch (err) {
      setTestResult({
        status: 'error',
        message: 'Failed to test connection',
        error: err.response?.data?.error || err.message
      });
    } finally {
      setTesting(false);
    }
  };

  const handleAutoSetup = async () => {
    setConfirmDialog({
      open: true,
      title: 'Automatic Microsoft App Setup',
      message: 'This will automatically create a new Microsoft App Registration. Continue?',
      variant: 'confirm',
      onConfirm: async () => {
        try {
          setError('');
          setSuccess('');
          
          const response = await api.post('/admin/oauth/microsoft/auto-setup');
          setSuccess('Microsoft app registration created successfully!');
          loadConfig();
        } catch (err) {
          setError('Auto-setup is not available. Please configure manually using the instructions below.');
        } finally {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });
        }
      }
    });
  };

  if (loading) {
    return (
      <PageLayout title="Microsoft OAuth Configuration">
        <div className="text-center">Loading...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Microsoft OAuth Configuration"
      subtitle="Configure Sign in with Microsoft for your users"
      backTo="/management"
      backLabel="Back to Management"
    >
      <div className="max-w-6xl">

      {/* Status Banner */}
      {config && (
        <div className={`mb-6 p-4 rounded-lg ${config.enabled ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`text-2xl ${config.enabled ? 'text-green-600' : 'text-yellow-600'}`}>
              {config.enabled ? '✅' : '⚠️'}
            </div>
            <div>
              <div className="font-semibold">
                {config.enabled ? 'Microsoft OAuth is Enabled' : 'Microsoft OAuth is Disabled'}
              </div>
              <div className="text-sm text-gray-600">
                {config.enabled 
                  ? 'Users can sign in with their Microsoft accounts' 
                  : 'Complete the configuration below to enable Microsoft login'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {success}
        </div>
      )}

      {/* Configuration Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Configuration</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client ID
            </label>
            <input
              type="text"
              value={formData.client_id}
              onChange={(e) => setFormData({...formData, client_id: e.target.value})}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
            />
            <p className="mt-1 text-xs text-gray-500">
              Application (client) ID from Azure AD app registration
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client Secret
            </label>
            <input
              type="password"
              value={formData.client_secret}
              onChange={(e) => setFormData({...formData, client_secret: e.target.value})}
              placeholder="Enter new client secret (leave blank to keep current)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
            />
            <p className="mt-1 text-xs text-gray-500">
              Client secret value from Azure AD (only needed when changing)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Allowed Email Domains (Optional)
            </label>
            <input
              type="text"
              value={formData.allowed_domains}
              onChange={(e) => setFormData({...formData, allowed_domains: e.target.value})}
              placeholder='["company.com", "partner.com"] or leave empty for all domains'
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
            />
            <p className="mt-1 text-xs text-gray-500">
              JSON array of allowed email domains. Leave empty to allow all Microsoft accounts.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="enabled"
              checked={formData.enabled}
              onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
            />
            <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
              Enable Microsoft OAuth login
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover font-medium"
            >
              Save Configuration
            </button>
            <button
              onClick={handleTest}
              disabled={testing || !formData.client_id}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium disabled:opacity-50"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
          </div>
        </div>
      </div>

      {/* Test Results */}
      {testResult && (
        <div className={`mb-6 p-4 rounded-lg border ${
          testResult.status === 'success' 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <h3 className="font-semibold mb-2">
            {testResult.status === 'success' ? '✅ Test Successful' : '❌ Test Failed'}
          </h3>
          <p className="text-sm">{testResult.message}</p>
          {testResult.details && (
            <pre className="mt-2 p-2 bg-white rounded text-xs overflow-auto">
              {JSON.stringify(testResult.details, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* Setup Instructions */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Setup Instructions</h2>
        
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-lg mb-2">Step 1: Register App in Azure AD</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>Go to <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Azure Portal - App Registrations</a></li>
              <li>Click "New registration"</li>
              <li>Enter a name: <code className="bg-gray-100 px-2 py-1 rounded">IDP SSO Authentication</code>
                <div className="mt-1 text-xs text-amber-600">
                  ⚠️ Don't use "Microsoft" in the name - Azure will reject it
                </div>
              </li>
              <li>Select account types: <strong>Accounts in any organizational directory and personal Microsoft accounts</strong></li>
              <li>Set Redirect URI:
                <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-200">
                  <div className="text-xs text-gray-600 mb-1">
                    <strong>Platform Type:</strong> Web (select from dropdown)
                  </div>
                  <div className="text-xs text-gray-600 mb-2">
                    <strong>Redirect URI:</strong>
                  </div>
                  <code className="text-sm break-all">
                    {window.location.origin}/api/auth/oauth/callback/microsoft
                  </code>
                  <div className="mt-2 text-xs text-amber-600">
                    ⚠️ Do NOT select "Single-page application (SPA)" or "Public client/native" - must be <strong>Web</strong>
                  </div>
                </div>
              </li>
              <li>Click "Register"</li>
            </ol>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">Step 2: Get Client ID and Secret</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>After registration, copy the <strong>Application (client) ID</strong></li>
              <li>Go to "Certificates & secrets" → "Client secrets" → "New client secret"</li>
              <li>Add a description and set expiration (recommended: 24 months)</li>
              <li>Click "Add" and <strong>immediately copy the secret VALUE</strong> (it won't be shown again)</li>
            </ol>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">Step 3: Configure API Permissions</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>Go to "API permissions"</li>
              <li>Click "Add a permission" → "Microsoft Graph" → "Delegated permissions"</li>
              <li>Add these permissions:
                <ul className="ml-6 mt-2 space-y-1">
                  <li>✓ openid</li>
                  <li>✓ profile</li>
                  <li>✓ email</li>
                  <li>✓ User.Read</li>
                </ul>
              </li>
              <li>Click "Add permissions"</li>
              <li>(Optional) Click "Grant admin consent" to skip user consent</li>
            </ol>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">Step 4: Enter Credentials Above</h3>
            <p className="text-sm text-gray-700">
              Paste the Client ID and Client Secret into the form above, enable the checkbox, and click "Save Configuration".
            </p>
          </div>
        </div>
      </div>

      {/* Diagnostics */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Diagnostics</h2>
        
        <div className="space-y-3 text-sm">
          <div className="flex justify-between py-2 border-b">
            <span className="font-medium">Provider Status:</span>
            <span className={config?.enabled ? 'text-green-600' : 'text-gray-500'}>
              {config?.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          
          <div className="flex justify-between py-2 border-b">
            <span className="font-medium">Client ID Configured:</span>
            <span className={config?.client_id && config.client_id !== 'YOUR_MICROSOFT_CLIENT_ID' ? 'text-green-600' : 'text-red-600'}>
              {config?.client_id && config.client_id !== 'YOUR_MICROSOFT_CLIENT_ID' ? 'Yes' : 'No'}
            </span>
          </div>
          
          <div className="flex justify-between py-2 border-b">
            <span className="font-medium">Authorization Endpoint:</span>
            <span className="text-gray-600 text-xs break-all">{config?.authorization_endpoint}</span>
          </div>
          
          <div className="flex justify-between py-2 border-b">
            <span className="font-medium">Token Endpoint:</span>
            <span className="text-gray-600 text-xs break-all">{config?.token_endpoint}</span>
          </div>
          
          <div className="flex justify-between py-2 border-b">
            <span className="font-medium">User Info Endpoint:</span>
            <span className="text-gray-600 text-xs break-all">{config?.userinfo_endpoint}</span>
          </div>
          
          <div className="flex justify-between py-2 border-b">
            <span className="font-medium">Scopes:</span>
            <span className="text-gray-600 text-xs">{config?.scopes}</span>
          </div>
          
          <div className="flex justify-between py-2">
            <span className="font-medium">Redirect URI:</span>
            <span className="text-gray-600 text-xs break-all">
              {window.location.origin}/api/auth/oauth/callback/microsoft
            </span>
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-sm mb-2">🔍 Troubleshooting Tips</h3>
          <ul className="text-xs text-gray-700 space-y-1">
            <li>• Ensure the redirect URI in Azure AD matches exactly (including https://)</li>
            <li>• Client secret expires - check expiration date in Azure AD</li>
            <li>• Test the connection after saving configuration</li>
            <li>• Check browser console for errors during login</li>
            <li>• Verify API permissions are granted in Azure AD</li>
          </ul>
        </div>
      </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Confirm"
        variant={confirmDialog.variant}
      />
    </PageLayout>
  );
};

export default MicrosoftOAuthConfig;
